class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupCartTab();
    this.subscribeToCartUpdates();
  }

  subscribeToCartUpdates() {
    // Listen for cart updates to refresh the tab
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.refreshTab();
      });
    }
  }

  refreshTab() {
    // Fetch fresh cart-drawer section and update tab + drawer content
    fetch(`${window.location.pathname}?sections=cart-drawer`)
      .then(response => response.json())
      .then(data => {
        if (data['cart-drawer']) {
          this.updateTab(data['cart-drawer']);
          this.updateDrawerContent(data['cart-drawer']);
        }
      })
      .catch(error => console.error('Error refreshing cart:', error));
  }

  updateDrawerContent(html) {
    const newDoc = new DOMParser().parseFromString(html, 'text/html');
    const newDrawerInner = newDoc.querySelector('.drawer__inner');
    const currentDrawerInner = this.querySelector('.drawer__inner');
    if (newDrawerInner && currentDrawerInner) {
      currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      // Re-attach close button event listener
      const closeTab = this.querySelector('.cart-drawer__close-tab');
      if (closeTab) {
        closeTab.addEventListener('click', () => this.close());
      }
    }
    // Update empty state class
    const newCartDrawer = newDoc.querySelector('cart-drawer');
    if (newCartDrawer) {
      if (newCartDrawer.classList.contains('is-empty')) {
        this.classList.add('is-empty');
      } else {
        this.classList.remove('is-empty');
      }
    }
  }

  setupCartTab() {
    const tab = this.querySelector('.cart-drawer__tab');
    if (tab) {
      tab.addEventListener('click', () => this.open(tab));
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Also update the tab
    this.updateTab(parsedState.sections['cart-drawer']);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.setupCartTab();
      this.open();
    });
  }

  updateTab(html) {
    const newDoc = new DOMParser().parseFromString(html, 'text/html');
    const newTab = newDoc.querySelector('.cart-drawer__tab');
    const currentTab = this.querySelector('.cart-drawer__tab');
    if (newTab && currentTab) {
      currentTab.innerHTML = newTab.innerHTML;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    super.updateQuantity(line, quantity, name, variantId);
  }

  renderContents(parsedState) {
    // Call parent method if it exists
    if (super.renderContents) {
      super.renderContents(parsedState);
    }

    // Update the tab
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.updateTab && parsedState.sections && parsedState.sections['cart-drawer']) {
      cartDrawer.updateTab(parsedState.sections['cart-drawer']);
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
